// API工具函数，用于处理JWT token和统一的错误处理
const resolveApiBaseUrl = () => {
  const envBaseUrl = process.env.REACT_APP_API_BASE_URL?.trim();
  if (envBaseUrl) {
    return envBaseUrl.replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    // 前端静态服务跑在 :3000 时，默认后端在同主机 :5000。
    if (port === '3000') {
      return `${protocol}//${hostname}:5000`;
    }
  }

  // 默认走同源路径，适配反向代理将 /api 转发到后端。
  return '';
};

const API_BASE_URL = resolveApiBaseUrl();

const buildApiUrl = (path) => {
  if (/^https?:\/\//.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
};

const parseJsonResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      `接口返回非 JSON 响应（HTTP ${response.status}）。请检查前端 API 地址配置是否正确。`
    );
  }
  return response.json();
};

// 获取存储的token
export const getToken = () => {
  return localStorage.getItem('access_token');
};

// 获取存储的用户信息
export const getUser = () => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

// 清除登录信息
export const clearAuth = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
};

// 检查是否已登录
export const isAuthenticated = () => {
  return !!getToken();
};

// 创建带有token的请求头
export const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// 登录函数
export const login = async (username, password) => {
  try {
    const response = await fetch(buildApiUrl('/api/login'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    const data = await parseJsonResponse(response);

    if (data.status === 'success') {
      // 保存token到localStorage
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      return { user: data.user, token: data.access_token };
    } else {
      throw new Error(data.message || '登录失败');
    }
  } catch (error) {
    console.error('登录错误:', error);
    throw error;
  }
};

// 统一的API请求函数
export const apiRequest = async (url, options = {}) => {
  const defaultOptions = {
    headers: getAuthHeaders(),
    ...options
  };

  try {
    const response = await fetch(buildApiUrl(url), defaultOptions);
    
    // 如果是401错误，清除token并重新登录
    if (response.status === 401) {
      clearAuth();
      // 触发重新登录
      window.location.reload();
      return null;
    }

    const data = await parseJsonResponse(response);

    // 对非 2xx 响应抛出错误，提取 FastAPI HTTPException 的 detail 信息
    if (!response.ok) {
      const errorMessage = data.detail || data.message || `请求失败 (HTTP ${response.status})`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error('API请求错误:', error);
    throw error;
  }
};


// 注销
export const logout = async () => {
  try {
    await apiRequest('/api/logout', {
      method: 'POST'
    });
  } catch (error) {
    console.error('注销请求失败:', error);
  } finally {
    clearAuth();
  }
}; 
